import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import { VehicleInspection, VehicleInspectionCreate, UnitType } from '../types';
import { useAuth } from '../context/AuthContext';

interface VehicleInspectionFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: VehicleInspectionCreate) => void;
  inspection?: VehicleInspection;
  isLoading?: boolean;
}

interface FormData extends VehicleInspectionCreate {}

const VehicleInspectionForm: React.FC<VehicleInspectionFormProps> = ({
  open,
  onClose,
  onSubmit,
  inspection,
  isLoading = false,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    unit_number: '',
    unit_type: 'TRUCK',
  });
  const [errors, setErrors] = useState<string[]>([]);

  // Initialize form data when editing
  useEffect(() => {
    if (inspection) {
      setFormData({
        unit_number: inspection.unit_number,
        unit_type: inspection.unit_type,
        cab_card: inspection.cab_card,
        insurance_card: inspection.insurance_card,
        eld_guide: inspection.eld_guide,
        paper_log: inspection.paper_log,
        ifta: inspection.ifta,
        tractor_annual: inspection.tractor_annual,
        fire_extinguisher: inspection.fire_extinguisher,
        triangles: inspection.triangles,
        hut_ny: inspection.hut_ny,
        authority: inspection.authority,
        trailer_card: inspection.trailer_card,
        trailer_annual: inspection.trailer_annual,
      });
    } else {
      // Reset form for new inspection
      setFormData({
        unit_number: '',
        unit_type: 'TRUCK',
      });
    }
    setErrors([]);
  }, [inspection, open]);

  const handleUnitTypeChange = (type: UnitType) => {
    // Clear all checklist fields when switching type
    setFormData({
      unit_number: formData.unit_number,
      unit_type: type,
    });
  };

  const handleCheckboxChange = (field: keyof FormData, value: boolean) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.unit_number.trim()) {
      newErrors.push('Unit number is required');
    }

    if (formData.unit_type === 'TRUCK') {
      // Check all truck fields are defined
      const truckFields = [
        'cab_card',
        'insurance_card',
        'eld_guide',
        'paper_log',
        'ifta',
        'tractor_annual',
        'fire_extinguisher',
        'triangles',
        'hut_ny',
        'authority',
      ];

      for (const field of truckFields) {
        if (formData[field as keyof FormData] === undefined) {
          newErrors.push(`Please select Yes or No for all truck inspection items`);
          break;
        }
      }
    } else if (formData.unit_type === 'TRAILER') {
      // Check all trailer fields are defined
      const trailerFields = ['trailer_card', 'trailer_annual'];

      for (const field of trailerFields) {
        if (formData[field as keyof FormData] === undefined) {
          newErrors.push(`Please select Yes or No for all trailer inspection items`);
          break;
        }
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const renderCheckboxField = (
    label: string,
    field: keyof FormData
  ) => {
    const value = formData[field];
    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          mb: 2,
          backgroundColor: value === undefined ? '#fff' : 'background.paper',
        }}
      >
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ fontWeight: 500 }}>{label}</FormLabel>
          <RadioGroup
            row
            value={value === undefined ? '' : value ? 'yes' : 'no'}
            onChange={(e) => handleCheckboxChange(field, e.target.value === 'yes')}
            sx={{ mt: 1 }}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {inspection ? 'Edit Vehicle Inspection' : 'Add Vehicle Inspection'}
      </DialogTitle>
      <DialogContent>
        <>
          {errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </Alert>
          )}

          {/* Unit Type Selection */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Unit Type</FormLabel>
            <RadioGroup
              row
              value={formData.unit_type}
              onChange={(e) => handleUnitTypeChange(e.target.value as UnitType)}
            >
              <FormControlLabel value="TRUCK" control={<Radio />} label="Truck" />
              <FormControlLabel value="TRAILER" control={<Radio />} label="Trailer" />
            </RadioGroup>
          </FormControl>

          {/* Unit Number */}
          <TextField
            fullWidth
            label="Unit Number"
            value={formData.unit_number}
            onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
            required
            margin="normal"
            inputProps={{ maxLength: 50 }}
            helperText="Enter the unit number (alphanumeric)"
          />

          {/* Inspection Checklist */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Inspection Checklist
            </Typography>
          </Box>

          {formData.unit_type === 'TRUCK' ? (
            <>
              {renderCheckboxField('Cab Card', 'cab_card')}
              {renderCheckboxField('Insurance Card', 'insurance_card')}
              {renderCheckboxField('ELD Guide', 'eld_guide')}
              {renderCheckboxField('Paper Log', 'paper_log')}
              {renderCheckboxField('IFTA', 'ifta')}
              {renderCheckboxField('Tractor Annual', 'tractor_annual')}
              {renderCheckboxField('Fire Extinguisher', 'fire_extinguisher')}
              {renderCheckboxField('Triangles', 'triangles')}
              {renderCheckboxField('HUT (NY)', 'hut_ny')}
              {renderCheckboxField('Authority', 'authority')}
            </>
          ) : (
            <>
              {renderCheckboxField('Trailer Card', 'trailer_card')}
              {renderCheckboxField('Trailer Annual', 'trailer_annual')}
            </>
          )}

          {/* Inspector Info */}
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Inspected By:</strong> {user?.first_name} {user?.last_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Date:</strong> {new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' })} {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' })}
            </Typography>
          </Box>
        </>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Inspection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VehicleInspectionForm;
